import React, { useEffect, useRef, useState } from 'react';
import CoachAvatar from '../CoachAvatar';
import './ProposedAction.css';

function coachAvatar(coachImageUrl, coachEmoji) {
  return (
    <CoachAvatar
      imageUrl={coachImageUrl}
      emoji={coachEmoji}
      identitySettled
      alt="Coach"
      imageClassName="proposed-action-coach-avatar"
    />
  );
}

/**
 * Coalesce consecutive "Change set N <field> from X to Y" descriptions that
 * share the same field + from-value + to-value into a single range entry,
 * e.g. "Change set 2 weight from 155 to 135" × 3 -> "Change weight for sets
 * 2 to 4 from 155 to 135". Non-matching actions (or breaks in the run) are
 * passed through untouched. Pure display helper — the underlying actions
 * (and their action_ids) remain individual for approve/reject calls.
 */
function coalesceActionsForDisplay(actions) {
  const SET_DESC_RE = /^Change set (\d+) (weight|reps) from (.+) to (.+)$/;
  const out = [];
  let run = null; // { field, from, to, indices: [...], iconKey, firstAction }
  const flush = () => {
    if (!run) return;
    if (run.indices.length === 1) {
      out.push({
        key: run.firstAction.action_id,
        icon: run.iconKey,
        text: run.firstAction.description,
      });
    } else {
      const sorted = [...run.indices].sort((a, b) => a - b);
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const isContiguous = sorted.every((v, i) => i === 0 || v === sorted[i - 1] + 1);
      const setsLabel = isContiguous
        ? `sets ${first} to ${last}`
        : `sets ${sorted.join(', ')}`;
      out.push({
        key: `range-${run.firstAction.action_id}`,
        icon: run.iconKey,
        text: `Change ${run.field} for ${setsLabel} from ${run.from} to ${run.to}`,
      });
    }
    run = null;
  };

  for (const [actionIndex, a] of actions.entries()) {
    const m = a.description && a.description.match(SET_DESC_RE);
    if (!m) {
      flush();
      out.push({ key: a.action_id || `missing-action-${actionIndex}`, icon: a.tool_name, text: a.description });
      continue;
    }
    const setIdx = parseInt(m[1], 10);
    const field = m[2];
    const from = m[3];
    const to = m[4];
    if (
      run &&
      run.field === field &&
      run.from === from &&
      run.to === to &&
      run.iconKey === a.tool_name
    ) {
      run.indices.push(setIdx);
    } else {
      flush();
      run = {
        field,
        from,
        to,
        indices: [setIdx],
        iconKey: a.tool_name,
        firstAction: a,
      };
    }
  }
  flush();
  return out;
}

/**
 * Inline approval UI for AI-proposed workout modifications.
 * Renders inside the exercise chat area.
 *
 * Two modes:
 *  1. Single action — pass `action`. Renders one card with Apply/Dismiss
 *     buttons that approves/rejects that one action.
 *  2. Batch — pass `actions` (array of >= 1 pending actions). Renders one
 *     combined card listing every change with a single Apply all / Dismiss
 *     all pair, calling the batch endpoints. This keeps the chat from
 *     stacking many approval prompts when the AI suggests several edits.
 *
 * Common props:
 * - workoutId, workoutExerciseId, backendService
 * - onResolved: () => void
 */
function ProposedAction({
  action,
  actions,
  workoutId,
  workoutExerciseId,
  backendService,
  onResolved,
  compact = false,
  externalLoading = false,
  onResolutionStart,
  onResolutionEnd,
  onDiscuss,
  coachImageUrl = null,
  coachEmoji = '🥋',
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [skipSummary, setSkipSummary] = useState(null);
  const initialMissingIds = Array.isArray(actions)
    ? actions.filter(item => item?.status === 'missing').map(item => item.action_id)
    : [];
  const [localStatus, setLocalStatus] = useState(() => (
    Array.isArray(actions) && actions.length > 0 && initialMissingIds.length === actions.length ? 'missing' : null
  ));
  const [unavailableActionIds, setUnavailableActionIds] = useState(() => new Set(initialMissingIds));
  const actionIdentityKey = Array.isArray(actions)
    ? actions.map(item => item?.action_id).filter(Boolean).join('|')
    : '';
  const previousActionIdsRef = useRef(new Set(actionIdentityKey ? actionIdentityKey.split('|') : []));

  useEffect(() => {
    if (!Array.isArray(actions)) return;
    const incomingIds = new Set(actionIdentityKey ? actionIdentityKey.split('|') : []);
    const identitiesOverlap = [...incomingIds].some(id => previousActionIdsRef.current.has(id));
    previousActionIdsRef.current = incomingIds;
    if (identitiesOverlap || incomingIds.size === 0) return;
    const incomingMissingIds = actions.filter(item => item?.status === 'missing').map(item => item.action_id);
    setUnavailableActionIds(new Set(incomingMissingIds));
    setLocalStatus(incomingMissingIds.length === actions.length && actions.length > 0 ? 'missing' : null);
    setSkipSummary(null);
    setError(null);
  // Action content can be refreshed without changing canonical identity. Only a
  // wholly new identity set releases component-local quarantine and notices.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionIdentityKey]);

  const isBatch = Array.isArray(actions) && actions.length > 0;
  const activeActions = isBatch ? actions.filter(item => !unavailableActionIds.has(item.action_id)) : [];
  const busy = isLoading || externalLoading;
  const resolutionIds = isBatch ? activeActions.map(item => item.action_id).filter(Boolean) : [action?.action_id].filter(Boolean);
  const hasCompleteBatchIdentity = !isBatch || (
    resolutionIds.length === activeActions.length
    && new Set(resolutionIds).size === activeActions.length
  );
  const notifyResolutionStart = () => resolutionIds.forEach(id => onResolutionStart?.(id));
  const notifyResolutionEnd = reconciled => resolutionIds.forEach(id => onResolutionEnd?.(id, { reconciled }));
  const confirmedResolution = (response, fallbackStatus) => {
    if (!Array.isArray(response?.results)) {
      const responseStatus = response?.status;
      const confirmed = response?.success !== false
        && response?.terminal !== false
        && (!responseStatus || responseStatus === fallbackStatus);
      return {
        actionIds: confirmed ? resolutionIds : [],
        status: confirmed ? fallbackStatus : (responseStatus || 'failed'),
      };
    }
    const terminalResults = response.results.filter(result => result?.terminal === true);
    // A canonical `missing` result is not retryable: quarantine its rendered ID
    // until the workout successfully refreshes instead of offering Apply again.
    // Pending/recoverable failures remain actionable and are deliberately not
    // included here.
    const missingResults = response.results.filter(result => result?.status === 'missing');
    const statuses = new Set(terminalResults.map(result => result.status));
    return {
      actionIds: terminalResults.map(result => result.action_id).filter(Boolean),
      ...(missingResults.length > 0 ? { missingActionIds: missingResults.map(result => result.action_id).filter(Boolean) } : {}),
      status: statuses.size === 1 && ['approved', 'rejected'].includes([...statuses][0])
        ? [...statuses][0]
        : (terminalResults.length > 0 ? 'resolved' : 'failed'),
    };
  };
  const reconcile = async (failureMessage, resolution) => {
    if (!onResolved) return true;
    try {
      const result = await onResolved(resolution);
      if (result === false) setError(failureMessage);
      return result !== false;
    } catch {
      setError(failureMessage);
      return false;
    }
  };
  const resolutionActions = actionIds => {
    const ids = new Set(actionIds || []);
    return (isBatch ? actions : [action])
      .filter(item => ids.has(item?.action_id))
      .map(item => ({
        ...item,
        arguments: {
          ...item?.arguments,
          workout_exercise_id: item?.arguments?.workout_exercise_id || workoutExerciseId,
        },
      }));
  };

  if (!isBatch && !action) return null;

  // ----- Batch mode -----
  if (isBatch) {
    const status = activeActions.length === 0 && unavailableActionIds.size > 0 ? 'missing' : localStatus;
    const isResolved = status === 'approved' || status === 'rejected' || status === 'skipped' || status === 'missing';

    const handleApproveAll = async () => {
      if (busy) return;
      if (!hasCompleteBatchIdentity) {
        setError('This suggestion changed before approval. Refresh and review the current changes.');
        return;
      }
      let reconciled = true;
      setIsLoading(true);
      setError(null);
      notifyResolutionStart();
      try {
        const res = await backendService.approveAllActions(workoutId, workoutExerciseId, resolutionIds);
        const resolution = confirmedResolution(res, 'approved');
        resolution.actions = resolutionActions(resolution.actionIds);
        // Backend returns success=false when any individual action failed (e.g.
        // AI proposed a change against a set_id that no longer exists). Surface
        // the failure instead of pretending everything was applied.
        if (res && res.success === false) {
          if (resolution.missingActionIds?.length > 0) {
            setUnavailableActionIds(current => new Set([...current, ...resolution.missingActionIds]));
            if (resolution.missingActionIds.length === resolutionIds.length) setLocalStatus('missing');
          }
          setError(res.skippedCount > 0 && res.failureCount > 0
            ? `${res.successCount || 0} applied; ${res.skippedCount} skipped; ${res.failureCount} failed. ${res.message || 'Review the remaining suggestions before retrying.'}`
            : res.successCount > 0 && res.failureCount > 0
              ? `${res.successCount} of ${resolutionIds.length} changes applied; ${res.failureCount} could not be applied. ${res.message || 'Review the remaining suggestions before retrying.'}`
              : (res.message || 'Some changes failed to apply'));
          reconciled = await reconcile(
            resolution.missingActionIds?.length > 0
              ? 'This suggestion is unavailable and current Coach guidance could not be refreshed.'
              : 'Some changes were processed, but the workout could not refresh.', {
            ...resolution, response: res,
          });
          return;
        }
        setLocalStatus(res?.skippedCount > 0 ? 'skipped' : 'approved');
        if (res?.skippedCount > 0) setSkipSummary(`${res.successCount ? `${res.successCount} applied; ` : ''}${res.skippedCount} suggestion${res.skippedCount === 1 ? '' : 's'} skipped without changing sets. See Workout Coach for details.`);
        reconciled = await reconcile('Changes were processed, but the workout could not refresh.', {
          ...resolution, response: res,
        });
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to apply changes');
      } finally {
        setIsLoading(false);
        notifyResolutionEnd(reconciled);
      }
    };

    const handleRefreshSuggestion = async () => {
      if (busy || !onResolved) return;
      setIsLoading(true);
      setError(null);
      try {
        const refreshed = await onResolved({
          refreshOnly: true,
          missingActionIds: [...unavailableActionIds],
          status: 'missing',
        });
        if (refreshed === false) setError('Current Coach guidance could not be refreshed. Try Refresh suggestion again.');
      } catch {
        setError('Current Coach guidance could not be refreshed. Try Refresh suggestion again.');
      } finally {
        setIsLoading(false);
      }
    };

    const handleRejectAll = async () => {
      if (busy) return;
      if (!hasCompleteBatchIdentity) {
        setError('This suggestion changed before dismissal. Refresh and review the current changes.');
        return;
      }
      let reconciled = true;
      setIsLoading(true);
      setError(null);
      notifyResolutionStart();
      try {
        const res = await backendService.rejectAllActions(workoutId, workoutExerciseId, resolutionIds);
        const resolution = confirmedResolution(res, 'rejected');
        if (res && res.success === false) {
          if (resolution.missingActionIds?.length > 0) {
            setUnavailableActionIds(current => new Set([...current, ...resolution.missingActionIds]));
            if (resolution.missingActionIds.length === resolutionIds.length) setLocalStatus('missing');
          }
          setError(res.message || 'Some changes changed before dismissal');
          reconciled = await reconcile(
            resolution.missingActionIds?.length > 0
              ? 'This suggestion is unavailable and current Coach guidance could not be refreshed.'
              : 'Some changes were processed, but the Coach thread could not refresh.', {
            ...resolution, response: res,
          });
          return;
        }
        setLocalStatus('rejected');
        reconciled = await reconcile('Changes were dismissed, but the Coach thread could not refresh.', {
          ...resolution, response: res,
        });
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to dismiss changes');
      } finally {
        setIsLoading(false);
        notifyResolutionEnd(reconciled);
      }
    };

    return (
      <div className={`proposed-action batch ${status === 'approved' ? 'approved' : ''} ${status === 'rejected' ? 'rejected' : ''}`}>
        <div className="proposed-action-header">
          <span className="proposed-action-icon">{coachAvatar(coachImageUrl, coachEmoji)}</span>
          <span className="proposed-action-label">
            {activeActions.length === 1 ? 'Suggested change' : `${activeActions.length} suggested changes`}
          </span>
        </div>
        <ul className="proposed-action-list">
          {coalesceActionsForDisplay(activeActions).map((entry) => (
            <li key={entry.key}>
              <span className="proposed-action-list-icon">{toolIcon(entry.icon)}</span>
              {entry.text}
            </li>
          ))}
        </ul>
        {error && <div className="proposed-action-error" role="alert">{error}</div>}
        {!isResolved && (
          <div className="proposed-action-buttons">
            <button
              className="proposed-action-btn approve"
              onClick={handleApproveAll}
              disabled={busy}
            >
              {busy ? '...' : (activeActions.length === 1 ? '✓ Apply' : '✓ Apply all')}
            </button>
            <button
              className="proposed-action-btn reject"
              onClick={handleRejectAll}
              disabled={busy}
            >
              {activeActions.length === 1 ? '✗ Dismiss' : '✗ Dismiss all'}
            </button>
            {onDiscuss && (
              <button
                type="button"
                className="proposed-action-btn discuss"
                onClick={onDiscuss}
                disabled={busy}
              >
                💬 Discuss
              </button>
            )}
          </div>
        )}
        {status === 'approved' && <div className="proposed-action-status approved">✓ Applied</div>}
        {status === 'skipped' && <div className="proposed-action-status superseded">{skipSummary}</div>}
        {status === 'rejected' && <div className="proposed-action-status rejected">✗ Dismissed</div>}
        {status === 'missing' && <div className="proposed-action-status rejected">Suggestion unavailable — refresh to review current Coach guidance.</div>}
        {status === 'missing' && onResolved && <button type="button" className="proposed-action-btn discuss" onClick={handleRefreshSuggestion} disabled={busy}>Refresh suggestion</button>}
      </div>
    );
  }

  // ----- Single action (history rendering) -----
  const status = localStatus || action.status;
  const isPending = status === 'pending';
  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';
  const isSuperseded = status === 'superseded';

  const handleApprove = async () => {
    if (busy) return;
    let reconciled = true;
    setIsLoading(true);
    setError(null);
    notifyResolutionStart();
    try {
      const res = await backendService.approveAction(workoutId, workoutExerciseId, action.action_id);
      if (res?.skipped === true && ['completed_set', 'deleted_set', 'stale_set'].includes(res?.reason) && res?.status === 'superseded' && res?.terminal === true) {
        setLocalStatus('superseded');
        setSkipSummary(res.reason === 'deleted_set' ? 'Skipped — set was removed' : res.reason === 'stale_set' ? 'Skipped — target set is unavailable' : 'Skipped — set already completed');
        reconciled = await reconcile('The set suggestion was skipped, but the workout could not refresh.', {
          actionIds: resolutionIds, status: 'superseded', actions: resolutionActions(resolutionIds), response: res,
        });
        return;
      }
      const resolution = confirmedResolution(res, 'approved');
      resolution.actions = resolutionActions(resolution.actionIds);
      if (resolution.status !== 'approved' || resolution.actionIds.length !== resolutionIds.length) {
        setError(res?.message || 'This change could not be confirmed. Refresh and review the current suggestion.');
        reconciled = await reconcile('The change was not confirmed and the workout could not refresh.', {
          ...resolution, response: res,
        });
        return;
      }
      setLocalStatus('approved');
      reconciled = await reconcile('Change was applied, but the workout could not refresh.', {
        ...resolution, response: res,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve');
    } finally {
      setIsLoading(false);
      notifyResolutionEnd(reconciled);
    }
  };

  const handleReject = async () => {
    if (busy) return;
    let reconciled = true;
    setIsLoading(true);
    setError(null);
    notifyResolutionStart();
    try {
      const res = await backendService.rejectAction(workoutId, workoutExerciseId, action.action_id);
      const resolution = confirmedResolution(res, 'rejected');
      if (resolution.status !== 'rejected' || resolution.actionIds.length !== resolutionIds.length) {
        setError(res?.message || 'This dismissal could not be confirmed. Refresh and review the current suggestion.');
        reconciled = await reconcile('The dismissal was not confirmed and the Coach thread could not refresh.', {
          ...resolution, response: res,
        });
        return;
      }
      setLocalStatus('rejected');
      reconciled = await reconcile('Change was dismissed, but the Coach thread could not refresh.', {
        ...resolution, response: res,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject');
    } finally {
      setIsLoading(false);
      notifyResolutionEnd(reconciled);
    }
  };

  if (compact) {
    return (
      <div className={`proposed-action proposed-action-compact ${isApproved ? 'approved' : ''} ${isRejected ? 'rejected' : ''}`}>
        <div className="proposed-action-description" title={action.description}>{action.description}</div>
        {isPending && (
          <div className="proposed-action-buttons">
            <button
              type="button"
              className="proposed-action-btn approve"
              aria-label="Apply Coach change"
              onClick={handleApprove}
              disabled={busy}
            >
              {busy ? '...' : '✓ Apply'}
            </button>
            <button
              type="button"
              className="proposed-action-btn reject"
              aria-label="Dismiss Coach change"
              onClick={handleReject}
              disabled={busy}
            >
              ✗ Dismiss
            </button>
            {onDiscuss && (
              <button type="button" className="proposed-action-btn discuss" onClick={onDiscuss} disabled={busy}>
                💬 Discuss
              </button>
            )}
          </div>
        )}
        {isApproved && <div className="proposed-action-status approved">✓ Applied</div>}
        {isSuperseded && (localStatus === 'superseded' || ['completed_set', 'deleted_set', 'stale_set'].includes(action.resolution_reason)) && <div className="proposed-action-status superseded">{skipSummary || (action.resolution_reason === 'deleted_set' ? 'Skipped — set was removed' : action.resolution_reason === 'stale_set' ? 'Skipped — target set is unavailable' : 'Skipped — set already completed')}</div>}
        {isRejected && <div className="proposed-action-status rejected">✗ Dismissed</div>}
        {error && <div className="proposed-action-error" role="alert">{error}</div>}
      </div>
    );
  }

  return (
    <div className={`proposed-action ${isApproved ? 'approved' : ''} ${isRejected ? 'rejected' : ''} ${isSuperseded ? 'superseded' : ''}`}>
      <div className="proposed-action-header">
        <span className="proposed-action-icon">{coachAvatar(coachImageUrl, coachEmoji)}</span>
        <span className="proposed-action-label">Suggested change</span>
      </div>
      <div className="proposed-action-description">{action.description}</div>
      {isPending && (
        <div className="proposed-action-buttons">
          <button
            className="proposed-action-btn approve"
            onClick={handleApprove}
            disabled={busy}
          >
            {busy ? '...' : '✓ Apply'}
          </button>
          <button
            className="proposed-action-btn reject"
            onClick={handleReject}
            disabled={busy}
          >
            ✗ Dismiss
          </button>
          {onDiscuss && (
            <button type="button" className="proposed-action-btn discuss" onClick={onDiscuss} disabled={busy}>
              💬 Discuss
            </button>
          )}
        </div>
      )}
      {isApproved && <div className="proposed-action-status approved">✓ Applied</div>}
      {isRejected && <div className="proposed-action-status rejected">✗ Dismissed</div>}
      {isSuperseded && <div className="proposed-action-status superseded">{skipSummary || (action.resolution_reason === 'deleted_set' ? 'Skipped — set was removed' : (action.resolution_reason === 'completed_set' ? 'Skipped — set already completed' : (action.resolution_reason === 'stale_set' ? 'Skipped — target set is unavailable' : '↻ Superseded')))}</div>}
      {error && <div className="proposed-action-error">{error}</div>}
    </div>
  );
}

function toolIcon(toolName) {
  switch (toolName) {
    case 'modify_set_weight': return '🏋️';
    case 'modify_set_reps': return '🔄';
    case 'add_set': return '➕';
    case 'remove_set': return '➖';
    case 'swap_exercise': return '🔀';
    case 'create_superset': return '🔗';
    case 'dissolve_superset': return '✂️';
    default: return '🤖';
  }
}

export default ProposedAction;
